/**
 * RouteHelmet.jsx
 * Sits inside <BrowserRouter> and automatically applies the correct
 * <head> tags for every public route using getSEO() from seoData.js.
 *
 * Admin routes (/admin/*) are intentionally skipped — the CMS shell
 * keeps the global noindex from public/index.html and never has its
 * <head> swapped.
 *
 * For pages with dynamic data (BlogDetailPage, CampusDetailPage), drop
 * a <PageSEO> directly inside the page component — react-helmet-async
 * merges and the most specific (later-mounted) wins.
 *
 * PERF Phase 2 (Feb 2026): PROGRAMS_CATALOG (used by seoData.js to
 * build JSON-LD for /programs/:slug) is now lazy-loaded via
 * ensureProgramSEO(). For non-program routes RouteHelmet stays fully
 * synchronous. For /programs/:slug routes we render sync fallback SEO
 * on first paint, kick off ensureProgramSEO(), then setState to
 * re-render with the full Course + EducationalOccupationalProgram
 * @graph. Prerender-cache still captures the post-resolve DOM because
 * Puppeteer waits for networkidle + WAIT_MS before capture — enough
 * time for the ~2 KB catalog chunk to arrive over localhost.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageSEO from '@/components/PageSEO';
import { getSEO, ensureProgramSEO } from '@/data/seoData';

// Known campus slugs — inlined (not imported from campusGbp) so a
// stale import can't accidentally break SEO on every /programs/*/*
// route by widening the "treat as combo" test. Add new campuses here
// in the same change as campusGbp.CAMPUS_GBP.
const KNOWN_CAMPUS_SLUGS = new Set([
  'toronto', 'north-york',
  'mississauga-dundas', 'mississauga-hurontario',
  'brampton', 'hamilton', 'kitchener',
  'london', 'windsor', 'brantford',
]);

export default function RouteHelmet() {
  const { pathname } = useLocation();

  // Skip admin entirely — user constraint: CMS pages are not part of the
  // SEO surface area, and the global noindex from index.html stays in force.
  const isAdmin = pathname.startsWith('/admin');
  // Skip blog detail pages — they mount their own <PageSEO> inside
  // BlogDetailPage.jsx with dynamic title, excerpt, image, and Article
  // JSON-LD. Rendering RouteHelmet here too would produce duplicate
  // <link rel="canonical"> and <meta name="description"> tags, which
  // react-helmet-async does not deduplicate across separate Helmet
  // instances. Letting the page own its <head> is the correct pattern.
  const isBlogDetail = /^\/blog\/[^/]+$/.test(pathname);
  // Skip /programs/<program>/<campus> combo routes — the combo template
  // mounts its own <PageSEO> with combo-specific title, description,
  // canonical, and EducationalOccupationalProgram + hasCourseInstance +
  // BreadcrumbList JSON-LD. Rendering RouteHelmet's parent-program SEO
  // here too would produce duplicate <title>/<link rel="canonical">.
  //
  // 2026-02 — tightened to match ONLY real campus slugs. The previous
  // /^\/programs\/[^/]+\/[^/]+$/ regex caught any 3-segment program
  // path, which incorrectly skipped the dedicated admissions-requirements
  // page at /programs/cardiology-technologist/cardio_admissions_requirements
  // and gave it an empty <title> after hydration (prerender fail
  // observed 2026-02). Only the second-segment-is-a-campus-slug case
  // is a real combo; everything else is a program sub-page and should
  // own its RouteHelmet-driven SEO like any other static route.
  const comboMatch = pathname.match(/^\/programs\/[^/]+\/([^/]+)\/?$/);
  const isProgramCampus = !!(comboMatch && KNOWN_CAMPUS_SLUGS.has(comboMatch[1]));

  // getSEO() is sync-safe: it returns the STATIC_SEO entry for non-program
  // routes, and a lightweight fallback for /programs/:slug when the lazy
  // catalog chunk hasn't loaded yet.
  const [seo, setSeo] = useState(() => (isAdmin || isBlogDetail || isProgramCampus ? null : getSEO(pathname)));

  // On program routes, hydrate the full SEO (with JSON-LD) once the
  // lazy programsCatalog chunk resolves. Cheap after first call — the
  // promise is cached in seoData.js.
  useEffect(() => {
    if (isAdmin || isBlogDetail || isProgramCampus) {
      setSeo(null);
      return;
    }
    setSeo(getSEO(pathname));
    if (/^\/programs\/[^/]+$/.test(pathname)) {
      let alive = true;
      ensureProgramSEO().then(() => {
        if (alive) setSeo(getSEO(pathname));
      });
      return () => { alive = false; };
    }
  }, [pathname, isAdmin, isBlogDetail, isProgramCampus]);

  if (!seo) return null;

  return (
    <PageSEO
      title={seo.title}
      description={seo.description}
      canonical={seo.canonical ?? pathname}
      og={seo.og ?? {}}
      jsonLd={seo.jsonLd ?? null}
      noIndex={seo.noIndex}
    />
  );
}
